/**
 * Server state for the kitchen board. Realtime is the primary signal: an event only says "something
 * changed", so the board refetches through REST and never trusts an event as the new ticket state.
 * Recovery, in order: refetch on `stream.ready` (every connect and reconnect), on `stream.resync`, when the
 * tab becomes visible again, and after every action. While the stream is not live, a slow poll keeps the
 * board honest; while it is live there is no polling at all.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useStaffSession } from '@/auth/react';
import { useToast } from '@/components/Toast';
import { useRealtimeEvent, useRealtimeState } from '@/realtime/react';
import { kitchenApi } from './api';
import type { KitchenItem, VoidItemInput } from './api';
import { describeKitchenError, describeReprint } from './wording';

/** Poll interval while the live stream is down. Not used while it is live. */
export const FALLBACK_POLL_MS = 30_000;
/** Events that arrive together become one refetch. */
export const COALESCE_MS = 100;

export const kitchenKeys = {
  tickets: (branchId: string | null) => ['kitchen', branchId, 'tickets'] as const,
  menu: (branchId: string | null) => ['kitchen', branchId, 'menu'] as const,
  assistance: (branchId: string | null) => ['kitchen', branchId, 'assistance'] as const,
};

function useBranchId(): string | null {
  return useStaffSession()?.user.branchId ?? null;
}

export function useTickets(enabled = true) {
  const branchId = useBranchId();
  const live = useRealtimeState() === 'live';
  return useQuery({
    queryKey: kitchenKeys.tickets(branchId),
    queryFn: ({ signal }) => kitchenApi.getTickets(signal),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: live ? false : FALLBACK_POLL_MS,
  });
}

/** The availability list. Only read while the availability view is open. */
export function useKitchenMenu(enabled = true) {
  const branchId = useBranchId();
  const live = useRealtimeState() === 'live';
  return useQuery({
    queryKey: kitchenKeys.menu(branchId),
    queryFn: ({ signal }) => kitchenApi.getMenu(signal),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: live ? false : FALLBACK_POLL_MS,
  });
}

export function useAssistance(enabled = true) {
  const branchId = useBranchId();
  const live = useRealtimeState() === 'live';
  return useQuery({
    queryKey: kitchenKeys.assistance(branchId),
    queryFn: ({ signal }) => kitchenApi.getAssistance(signal),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: live ? false : FALLBACK_POLL_MS,
  });
}

/**
 * Maps staff stream events to refetches while the kitchen board is open. Duplicate and bursty events are
 * harmless: they only mark a query for refetch, and marks made within `COALESCE_MS` run once.
 * `menu.availability_changed` refreshes the availability list (another kitchen screen or a manager changed
 * an item).
 */
export function useKitchenLive(enabled = true) {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const pending = useRef({ tickets: false, assistance: false, menu: false });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
      timer.current = undefined;
    },
    [],
  );

  const mark = (what: 'tickets' | 'assistance' | 'menu') => {
    if (!enabled) return;
    pending.current[what] = true;
    if (timer.current !== undefined) return;
    timer.current = setTimeout(() => {
      timer.current = undefined;
      const due = pending.current;
      pending.current = { tickets: false, assistance: false, menu: false };
      if (due.tickets)
        void queryClient.invalidateQueries({ queryKey: kitchenKeys.tickets(branchId) });
      if (due.assistance)
        void queryClient.invalidateQueries({ queryKey: kitchenKeys.assistance(branchId) });
      if (due.menu) void queryClient.invalidateQueries({ queryKey: kitchenKeys.menu(branchId) });
    }, COALESCE_MS);
  };
  const both = () => {
    mark('tickets');
    mark('assistance');
  };
  const everything = () => {
    both();
    mark('menu');
  };

  useRealtimeEvent('order.created', () => mark('tickets'));
  useRealtimeEvent('order.item_status_changed', () => mark('tickets'));
  useRealtimeEvent('order.cancelled', () => mark('tickets'));
  useRealtimeEvent('order.item_voided', () => mark('tickets'));
  useRealtimeEvent('session.transferred', () => mark('tickets'));
  useRealtimeEvent('session.closed', both);
  useRealtimeEvent('assistance.requested', () => mark('assistance'));
  useRealtimeEvent('assistance.resolved', () => mark('assistance'));
  useRealtimeEvent('menu.availability_changed', () => mark('menu'));
  useRealtimeEvent('stream.resync', everything);
  useRealtimeEvent('stream.ready', everything);
}

/**
 * Refetches the board. After a success the promise is returned, so the action stays "pending" until the
 * board is fresh. After a failure the refetch runs in the background: the person is told at once and the
 * button is free again while the board catches up.
 */
function refreshTickets(queryClient: QueryClient, branchId: string | null, failed = false) {
  const refetch = queryClient.invalidateQueries({ queryKey: kitchenKeys.tickets(branchId) });
  return failed ? undefined : refetch;
}

type Step = 'IN_PREPARATION' | 'READY';

/**
 * Moves items of one ticket one step each, one request at a time, in the order given (one item is the
 * usual case). Nothing is shown optimistically: the board changes only when the refetch after the calls
 * returns, whether they worked or the backend refused one. It stops at the first refusal; items already
 * moved stay moved, and the refetch shows exactly where things stand.
 *
 * Toasts live here, not in the caller: a ticket can leave the board while a request is in flight (a waiter
 * serves it, the guest cancels it), unmounting the component that pressed the button, and a refusal must
 * still be explained.
 */
export function useAdvanceItems() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: async (vars: { items: Pick<KitchenItem, 'order_item_id'>[]; to: Step }) => {
      for (const item of vars.items) {
        await kitchenApi.updateItemStatus(item.order_item_id, vars.to);
      }
    },
    onError: (error) =>
      toast.show(describeKitchenError(error), { tone: 'error', durationMs: 7000 }),
    onSettled: (_data, error) => refreshTickets(queryClient, branchId, error !== null),
  });
}

/**
 * Turns an item on or off for this branch. Sent once, never retried; after any answer the list is read again
 * and the action stays pending until it is, so the screen shows the server's state, not a guess.
 */
export function useSetAvailability() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    retry: false,
    mutationFn: (vars: { menuItemId: string; name: string; isAvailable: boolean }) =>
      kitchenApi.setAvailability(vars.menuItemId, vars.isAvailable),
    onSuccess: (result, vars) =>
      toast.show(
        result.is_available
          ? `${vars.name} is available again.`
          : `${vars.name} is now unavailable.`,
        { tone: 'success', durationMs: 3000 },
      ),
    onError: (error) =>
      toast.show(describeKitchenError(error), { tone: 'error', durationMs: 7000 }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: kitchenKeys.menu(branchId) }),
  });
}

export function useVoidItem() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { itemId: string; itemName: string; input: VoidItemInput }) =>
      kitchenApi.voidItem(vars.itemId, vars.input),
    onSuccess: (_result, vars) => toast.show(`${vars.itemName} voided.`, { tone: 'success' }),
    onSettled: (_data, error) => refreshTickets(queryClient, branchId, error !== null),
  });
}

/** A finished reprint is reported from the print job's own status: a failed print is never shown as success. */
export function useReprintTicket() {
  const toast = useToast();
  return useMutation({
    mutationFn: (vars: { orderId: string; idempotencyKey: string }) =>
      kitchenApi.reprintTicket(vars.orderId, vars.idempotencyKey),
    onSuccess: (result) => {
      const outcome = describeReprint(result.status);
      toast.show(outcome.message, {
        tone: outcome.ok ? 'success' : 'error',
        durationMs: outcome.ok ? 3000 : 8000,
      });
    },
    onError: (error) =>
      toast.show(describeKitchenError(error), { tone: 'error', durationMs: 8000 }),
  });
}

export function useResolveAssistance() {
  const queryClient = useQueryClient();
  const branchId = useBranchId();
  const toast = useToast();
  return useMutation({
    mutationFn: (requestId: string) => kitchenApi.resolveAssistance(requestId),
    onError: (error) =>
      toast.show(describeKitchenError(error), { tone: 'error', durationMs: 7000 }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: kitchenKeys.assistance(branchId) }),
  });
}
